import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  buildWaLink, normalizePkMobile, pickBucket, renderTemplate, fmtPKRPlain,
} from "../lib/whatsappLink";
import { toast } from "sonner";
import WhatsAppComposeDialog from "./WhatsAppComposeDialog";

/**
 * Floating "Send WhatsApp" button.
 * Props:
 *   customer: { name, mobile }
 *   loan: { installment_value, next_due:{amount,due_date}, status, current_status, ... }
 *   variant?: "default" | "icon" | "ghost" — visual style
 *   className?: string
 *   testid?: string
 *   defaultBucket?: "before_due" | "due_today" | "overdue"
 */
export default function SendWhatsAppButton({
  customer, loan, variant = "default", className = "", testid, defaultBucket,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [bucket, setBucket] = useState(defaultBucket || "before_due");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open || templates) return;
    api.get("/whatsapp/templates")
      .then((r) => setTemplates(r.data))
      .catch((err) => console.warn("[wa] template load failed:", err?.message || err));
  }, [open, templates]);

  useEffect(() => {
    if (!open) return;
    setBucket(defaultBucket || pickBucket(loan));
  }, [open, defaultBucket, loan]);

  useEffect(() => {
    if (!templates || !open) return;
    const amount = Math.round(loan?.next_due?.amount ?? loan?.installment_value ?? 0);
    const dueDate = loan?.next_due?.due_date || loan?.start_date || "";
    setMessage(renderTemplate(templates[bucket] || "", {
      name: customer?.name || "",
      amount: fmtPKRPlain(amount),
      date: dueDate,
      shop: user?.shop_name || "",
    }));
  }, [bucket, templates, customer, loan, user, open]);

  const link = buildWaLink(customer?.mobile, message);
  const numOk = !!normalizePkMobile(customer?.mobile);

  const sendClick = (e) => {
    if (!numOk) {
      e.preventDefault();
      toast.error("Customer mobile not set");
      return;
    }
    setOpen(false);
  };

  const buttonCommonProps = {
    onClick: () => setOpen(true),
    "data-testid": testid || "send-wa-btn",
  };

  return (
    <>
      {variant === "icon" && (
        <Button
          {...buttonCommonProps}
          size="sm"
          variant="ghost"
          className={`text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 ${className}`}
          title="Send WhatsApp"
        >
          <MessageCircle className="w-4 h-4" />
        </Button>
      )}
      {variant === "ghost" && (
        <Button
          {...buttonCommonProps}
          size="sm"
          variant="ghost"
          className={`text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 ${className}`}
        >
          <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
        </Button>
      )}
      {variant === "default" && (
        <Button
          {...buttonCommonProps}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white ${className}`}
        >
          <MessageCircle className="w-4 h-4 mr-1" /> Send WhatsApp
        </Button>
      )}
      <WhatsAppComposeDialog
        open={open}
        onOpenChange={setOpen}
        customer={customer}
        bucket={bucket}
        setBucket={setBucket}
        message={message}
        setMessage={setMessage}
        numOk={numOk}
        link={link}
        onSend={sendClick}
      />
    </>
  );
}
